/**
 * CallCentreLiveClient: the call centre's live board (console) and caller badges (portal).
 *
 * Hub: `{dashboardsUrl}/hubs/dashboard/callcentre`. Any signed-in user may connect: a caller only ever receives their
 * own `badges`; console staff also receive the overall `board` and `online` (callers on shift) and may `joinCampaign`
 * to follow one campaign's board.
 *
 * Unlike `DomainDashboardClient`, this client resyncs itself after an automatic reconnect: it subscribes again (the
 * server re-joins the admin group and re-sends the snapshot) and re-joins every campaign it had joined, then emits
 * `resynced`. Pushes are whole values, so nothing needs reconciling.
 */
import { BaseSignalRClient } from '../shared/BaseSignalRClient';
import { ConnectionState } from '../shared/types';
/** Hub path under the Dashboards WebSocket base URL. */
export const CallCentreLiveHubPath = '/hubs/dashboard/callcentre';
export class CallCentreLiveClient extends BaseSignalRClient {
    /**
     * @param dashboardsUrl - the Dashboards WebSocket base (e.g. `https://ccms.actschurch.co.za/ws/dashboards`, or `/ws/dashboards`)
     * @param getToken - the signed-in user's access token
     */
    constructor(dashboardsUrl, getToken, options = {}) {
        super(`${dashboardsUrl.replace(/\/+$/, '')}${CallCentreLiveHubPath}`, getToken, options);
        this.subscribed = false;
        this.joinedCampaigns = new Set();
        this.lastState = ConnectionState.Disconnected;
        this.onStateChange((state) => {
            const wasReconnecting = this.lastState === ConnectionState.Reconnecting;
            this.lastState = state;
            if (state === ConnectionState.Connected && wasReconnecting)
                void this.resync();
        });
    }
    setupServerMethods() {
        if (!this.connection)
            return;
        this.connection.on('Subscribed', (m) => this.emit('subscribed', m));
        this.connection.on('badges', (m) => this.emit('badges', m));
        this.connection.on('board', (m) => this.emit('board', m));
        this.connection.on('online', (m) => this.emit('online', m));
    }
    /** Start receiving pushes; the server answers with `subscribed` and the current snapshot (`badges`, `board`, `online`). */
    async subscribe() {
        await this.invoke('SubscribeToDashboard', null, 30);
        this.subscribed = true;
    }
    /** Stop receiving pushes (the connection stays open). */
    async unsubscribe() {
        this.subscribed = false;
        await this.invoke('UnsubscribeFromDashboard');
    }
    /** The current snapshot as a return value. */
    async getSnapshot() {
        return await this.invoke('GetSnapshot');
    }
    /** Re-send the snapshot as pushes (and re-join the admin group). */
    async requestSnapshot() {
        await this.invoke('RequestBadgeSnapshot');
    }
    /**
     * Follow one campaign's board (console staff only). Resolves to the board now, or null when not allowed or not known
     * yet. Remembered, so it is re-joined after a reconnect.
     */
    async joinCampaign(campaignId) {
        this.joinedCampaigns.add(campaignId);
        return (await this.invoke('JoinCampaign', campaignId)) ?? null;
    }
    /** Stop following a campaign's board. */
    async leaveCampaign(campaignId) {
        this.joinedCampaigns.delete(campaignId);
        if (this.isConnected())
            await this.invoke('LeaveCampaign', campaignId);
    }
    /** Campaigns this client follows. */
    getJoinedCampaigns() {
        return [...this.joinedCampaigns];
    }
    /** After an automatic reconnect: a new connection id has no groups, so subscribe and re-join everything. */
    async resync() {
        try {
            if (this.subscribed)
                await this.invoke('SubscribeToDashboard', null, 30);
            for (const id of this.joinedCampaigns)
                await this.invoke('JoinCampaign', id);
            this.emit('resynced', undefined);
        }
        catch {
            // Quiet: the next reconnect (or the caller's REST refresh) covers it.
        }
    }
}
//# sourceMappingURL=CallCentreLiveClient.js.map